"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getLimit = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const max_1 = __importDefault(require("lodash/max"));
const api_1 = require("../../../../api");
const bignumber_1 = require("../../../utils/bignumber");
const constants_1 = require("../../../utils/constants");
const constants_2 = require("../../constants");
const constants_3 = require("../constants");
const getInboundAddressDataForChain_1 = require("../getInboundAddressDataForChain");
const getTradeRate_1 = require("../getTradeRate/getTradeRate");
const getUsdRate_1 = require("../getUsdRate/getUsdRate");
const isRune_1 = require("../isRune/isRune");
const getLimit = async ({ sellAsset, buyAssetId, sellAmountCryptoBaseUnit, deps, slippageTolerance, buyAssetTradeFeeUsd, }) => {
    var _a, _b;
    const tradeRate = await (0, getTradeRate_1.getTradeRate)(sellAsset, buyAssetId, sellAmountCryptoBaseUnit, deps);
    const sellAssetChainFeeAssetId = (_a = deps.adapterManager.get(sellAsset.chainId)) === null || _a === void 0 ? void 0 : _a.getFeeAssetId();
    const buyAssetChainFeeAssetId = (_b = deps.adapterManager
        .get((0, caip_1.fromAssetId)(buyAssetId).chainId)) === null || _b === void 0 ? void 0 : _b.getFeeAssetId();
    if (!sellAssetChainFeeAssetId || !buyAssetChainFeeAssetId) {
        throw new api_1.SwapError('[getLimit]: no sellAssetChainFeeAsset or buyAssetChainFeeAssetId', {
            code: api_1.SwapErrorTypes.BUILD_TRADE_FAILED,
            details: { sellAssetChainFeeAssetId, buyAssetChainFeeAssetId },
        });
    }
    const sellFeeAssetUsdRate = await (0, getUsdRate_1.getUsdRate)(deps.daemonUrl, sellAssetChainFeeAssetId);
    const buyAssetUsdRate = await (0, getUsdRate_1.getUsdRate)(deps.daemonUrl, buyAssetId);
    const expectedBuyAmountCryptoPrecision8 = (0, bignumber_1.toBaseUnit)((0, bignumber_1.fromBaseUnit)((0, bignumber_1.bnOrZero)(sellAmountCryptoBaseUnit).times(tradeRate), sellAsset.precision), constants_3.THORCHAIN_FIXED_PRECISION);
    const isValidSlippageRange = (0, bignumber_1.bnOrZero)(slippageTolerance).gte(0) && (0, bignumber_1.bnOrZero)(slippageTolerance).lte(1);
    if ((0, bignumber_1.bnOrZero)(expectedBuyAmountCryptoPrecision8).lt(0) || !isValidSlippageRange)
        throw new api_1.SwapError('[getLimit]: bad expected buy amount or bad slippage tolerance', {
            code: api_1.SwapErrorTypes.BUILD_TRADE_FAILED,
            details: { expectedBuyAmountCryptoPrecision8, slippageTolerance },
        });
    const buyAssetTradeFeeCryptoPrecision8 = (0, bignumber_1.toBaseUnit)((0, bignumber_1.bnOrZero)(buyAssetTradeFeeUsd).div(buyAssetUsdRate), constants_3.THORCHAIN_FIXED_PRECISION);
    const sellAssetAddressData = await (0, getInboundAddressDataForChain_1.getInboundAddressDataForChain)(deps.daemonUrl, sellAsset.assetId);
    const refundFeeBuyAssetCryptoPrecision8 = (() => {
        switch (true) {
            // If the sell asset is on THOR the return fee is fixed at 0.02 RUNE
            case (0, isRune_1.isRune)(sellAsset.assetId): {
                const runeFeeUsd = constants_2.RUNE_OUTBOUND_TRANSACTION_FEE_CRYPTO_HUMAN.times(sellFeeAssetUsdRate);
                return (0, bignumber_1.toBaseUnit)((0, bignumber_1.bnOrZero)(runeFeeUsd).div(buyAssetUsdRate), constants_3.THORCHAIN_FIXED_PRECISION);
            }
            // Else the return fee is the outbound fee of the sell asset's chain
            default: {
                const sellAssetTradeFeeCryptoHuman = (0, bignumber_1.fromBaseUnit)((0, bignumber_1.bnOrZero)(sellAssetAddressData === null || sellAssetAddressData === void 0 ? void 0 : sellAssetAddressData.outbound_fee), constants_3.THORCHAIN_FIXED_PRECISION);
                const sellAssetTradeFeeUsd = (0, bignumber_1.bnOrZero)(sellAssetTradeFeeCryptoHuman).times(sellFeeAssetUsdRate);
                return (0, bignumber_1.toBaseUnit)((0, bignumber_1.bnOrZero)(sellAssetTradeFeeUsd).div(buyAssetUsdRate), constants_3.THORCHAIN_FIXED_PRECISION);
            }
        }
    })();
    const highestPossibleFeeCryptoPrecision8 = (0, max_1.default)([
        // both fees are denominated in buy asset crypto precision 8
        (0, bignumber_1.bnOrZero)(buyAssetTradeFeeCryptoPrecision8).toNumber(),
        (0, bignumber_1.bnOrZero)(refundFeeBuyAssetCryptoPrecision8).toNumber(),
    ]);
    const expectedBuyAmountAfterHighestFeeCryptoPrecision8 = (0, bignumber_1.bnOrZero)(expectedBuyAmountCryptoPrecision8)
        .times((0, bignumber_1.bn)(1).minus(slippageTolerance))
        .times((0, bignumber_1.bn)(1).minus(constants_1.ALLOWABLE_MARKET_MOVEMENT))
        .minus(highestPossibleFeeCryptoPrecision8 !== null && highestPossibleFeeCryptoPrecision8 !== void 0 ? highestPossibleFeeCryptoPrecision8 : 0)
        .decimalPlaces(0);
    // expectedBuyAmountAfterHighestFeeCryptoPrecision8 can be negative if the sell asset has a higher inbound_fee
    // (a refund) than the buy asset's inbound_fee + buy amount.
    // I.e. we've come this far - we don't have enough to refund, so the limit can slide all the way to 0.
    return expectedBuyAmountAfterHighestFeeCryptoPrecision8.isPositive()
        ? expectedBuyAmountAfterHighestFeeCryptoPrecision8.toString()
        : '0';
};
exports.getLimit = getLimit;
